// Riding along in somebody else's car: which car you may get into, and where you end up
// when you get out. Pure — no Phaser, no realtime — so `node --test` can check the two
// decisions that are easy to get subtly wrong and impossible to notice until two people
// are on the same street.
//
// The scene owns everything else: the server's `car_passengers` broadcast is the ONLY
// truth about who is aboard (a passenger's own `move` comes back downgraded to a walk),
// and it is the scene that hides the character, parks the walker and moves the camera.

import { NEAR } from './carCatalog.mjs';
import { STREET_SPAWN_Y } from '../streetSpawns.mjs';

const clamp = (n, lo, hi) => (n < lo ? lo : n > hi ? hi : n);

// Getting out: far enough sideways to be clear of the bodywork, and back on the PAVEMENT —
// the lanes are the road, and a passenger dropped in one would be standing in traffic.
// Sideways is the same distance the driver uses (half a body + a gap) but towards the
// car's BACK, so driver and passenger never land on the same pixel when both step out.
export const EXIT_ASIDE = 200;
export const EXIT_MARGIN = 60;   // matches CarController's EDGE_MARGIN
export const EXIT_Y = STREET_SPAWN_Y;   // the pedestrian's sprite-centre y on the pavement

/**
 * The stopped, driven car you may get into, or null.
 *
 * `avatars` is whatever the scene can say about the other people on the street:
 * `{ carId, name, x, y, speed, mode }` each. Only a peer the server currently reports
 * as a DRIVER (`mode === 'car'` with a `carId`) counts, and only while their last
 * broadcast said they were standing still — the server refuses a moving car with
 * CAR_MOVING, so offering the action would just be a button that always fails.
 *
 * Nearest wins, so a kerb full of cars still gives you the one you walked up to.
 */
export function boardableCar(avatars, me, { near = NEAR } = {}) {
  if (!me) return null;
  let best = null;
  let bestD = Infinity;
  for (const a of avatars ?? []) {
    if (!a || a.mode !== 'car' || !a.carId) continue;
    if ((a.speed ?? 0) !== 0) continue;
    const d = Math.hypot((a.x ?? 0) - me.x, (a.y ?? 0) - me.y);
    if (d <= near && d < bestD) {
      bestD = d;
      best = { carId: a.carId, name: a.name ?? '' };
    }
  }
  return best;
}

/**
 * Where a passenger stands after getting out of a car that stopped at `carX` facing
 * `facing`. Steps out towards the car's back; if that would put them through the end of the
 * world, out the front instead, and never outside the street either way. The y is always the
 * pavement: which lane the car was in decides nothing about where a person may stand.
 */
export function exitPosition(carX, facing, worldWidth = 6395) {
  const dir = facing === 'left' ? -1 : 1;
  let x = carX - EXIT_ASIDE * dir;                    // behind the car
  if (x < EXIT_MARGIN || x > worldWidth - EXIT_MARGIN) x = carX + EXIT_ASIDE * dir;
  return { x: clamp(x, EXIT_MARGIN, worldWidth - EXIT_MARGIN), y: EXIT_Y };
}
